class FilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.backdrop = null;
    this.drawer = null;
  }

  connectedCallback() {
    this.initializeComponent();
  }

  initializeComponent() {
    this.drawer = this.querySelector('.filter-drawer');

    if (this.drawer) {
      // Content is ready, initialize everything
      this.createBackdrop();
      this.setupEventListeners();
      this.setupAccessibility();
    } else {
      // Content not ready yet, wait for it
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.drawer = this.querySelector('.filter-drawer');
            if (this.drawer) {
              observer.disconnect(); // Stop observing
              this.createBackdrop();
              this.setupEventListeners();
              this.setupAccessibility();
            }
          }
        });
      });

      // Start observing for content changes
      observer.observe(this, { childList: true, subtree: true });
    }
  }

  createBackdrop() {
    // Check if backdrop already exists to prevent duplicates
    if (this.backdrop || document.querySelector('.filter-drawer-backdrop')) {
      this.backdrop = document.querySelector('.filter-drawer-backdrop');
      return;
    }

    this.backdrop = document.createElement('div');
    this.backdrop.className = 'filter-drawer-backdrop';
    document.body.appendChild(this.backdrop);
  }

  setupEventListeners() {
    // Trigger button clicks
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-filter-drawer-trigger]')) {
        e.preventDefault();
        this.open();
      }
    });

    // Close button clicks
    const closeBtn = this.querySelector('[data-filter-drawer-close]');
    closeBtn?.addEventListener('click', () => this.close());

    // Backdrop clicks
    this.backdrop?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setupAccessibility() {
    if (this.drawer) {
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.drawer.setAttribute('aria-labelledby', 'filter-drawer-title');
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.classList.add('overflow-hidden-mobile');

    // Show backdrop
    if (this.backdrop) {
      this.backdrop.classList.add('show');
    }

    // Show drawer
    if (this.drawer) {
      this.drawer.classList.add('open');
    }

    // Setup focus trapping with a small delay to ensure drawer is visible
    setTimeout(() => {
      this.trapFocus();
    }, 100);

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('filter-drawer:opened'));
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Restore body scroll
    document.body.style.overflow = '';
    document.body.classList.remove('overflow-hidden-mobile');

    // Remove focus trap
    if (this.tabHandler) {
      document.removeEventListener('keydown', this.tabHandler);
      this.tabHandler = null;
    }

    // Hide backdrop
    if (this.backdrop) {
      this.backdrop.classList.remove('show');
    }

    // Hide drawer
    if (this.drawer) {
      this.drawer.classList.remove('open');
    }

    // Return focus to trigger button
    const triggerBtn = document.querySelector('[data-filter-drawer-trigger]');
    triggerBtn?.focus();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('filter-drawer:closed'));
  }

  trapFocus() {
    const focusableElements = this.drawer?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements?.length) {
      return;
    }

    const focusableArray = Array.from(focusableElements);
    const firstElement = focusableArray[0];
    const lastElement = focusableArray[focusableArray.length - 1];

    // Focus first element
    firstElement.focus();

    // Store the tab handler so we can remove it later
    this.tabHandler = (e) => {
      // Only handle Tab key when drawer is open and focus is inside the drawer
      if (e.key !== 'Tab' || !this.isOpen) return;

      // Check if focus is currently inside the drawer
      if (!this.drawer.contains(document.activeElement)) {
        // Focus escaped, bring it back to first element
        firstElement.focus();
        e.preventDefault();
        return;
      }

      const currentIndex = focusableArray.indexOf(document.activeElement);

      if (e.shiftKey) {
        // Shift + Tab (backward)
        if (currentIndex === 0) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        // Tab (forward)
        if (currentIndex === focusableArray.length - 1) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    // Add the event listener
    document.addEventListener('keydown', this.tabHandler);
  }

  disconnectedCallback() {
    // Cleanup tab handler
    if (this.tabHandler) {
      document.removeEventListener('keydown', this.tabHandler);
    }

    // Cleanup backdrop
    if (this.backdrop && this.backdrop.parentNode) {
      this.backdrop.parentNode.removeChild(this.backdrop);
    }
  }
}

customElements.define('filter-drawer', FilterDrawer);